import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { FALLBACK_ERROR_MESSAGE } from "@/lib/constants";
import { reDeploy } from "@/lib/deploy/api";
import { DEPLOY_STATUS } from "@/lib/enums";
import { UpdateDeploySettingsFormData } from "@/lib/validations";
import { Deploy } from "@/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import {
  AlertTriangle,
  CircleDot,
  Clock,
  GitBranch,
  GitCommit,
  Globe,
} from "lucide-react";
import { useState } from "react";

interface RedeployConfirmDialogProps {
  deploy: Deploy;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RedeployConfirmDialog({
  deploy,
  open,
  onOpenChange,
}: RedeployConfirmDialogProps) {
  const [isRedeploying, setIsRedeploying] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const updateDeployMutation = useMutation({
    mutationFn: reDeploy,
    onSuccess: (data) => {
      queryClient.setQueryData(["deploys"], (old: any) => [
        data,
        ...(old || []),
      ]);
      queryClient.invalidateQueries({ queryKey: ["deploys"] });
      queryClient.invalidateQueries({ queryKey: ["deploy", deploy.id] });
      toast({
        title: "Redeploy started",
        description: "Deploy Queued.",
      });
      onOpenChange(false);
    },
    onError: (error: any) => {
      setIsRedeploying(false);

      toast({
        title: "Error",
        description: error?.response?.data?.message ?? FALLBACK_ERROR_MESSAGE,
        variant: "destructive",
      });
    },
  });

  const handleRedeploy = async () => {
    setIsRedeploying(true);
    const redeploy = {
      id: deploy.id,
      env: deploy.env || "staging",
      useBuildCache: false,
    } as UpdateDeploySettingsFormData;

    updateDeployMutation.mutate(redeploy);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-[#0B0E14] border-slate-800 text-white p-0 gap-0 overflow-hidden">
        <div className="p-6 space-y-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">
              Redeploy
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a new deployment with the same source code as your current
              one but with the latest Project Settings.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="border border-slate-700 rounded-lg p-4 bg-slate-900/50">
              <div className="flex items-start gap-3">
                <div className="mt-1">
                  <CircleDot className="h-4 w-4 text-white fill-current" />
                </div>
                <div className="flex-1 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-200 truncate max-w-[200px] sm:max-w-xs">
                        {deploy.repoName}
                      </span>
                      <Badge className="bg-slate-800 text-slate-300 hover:bg-slate-700 border-slate-700">
                        Current
                      </Badge>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-sm text-slate-300">
                      <GitBranch className="h-4 w-4 text-slate-500" />
                      <span>{deploy.branch}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-300">
                      <GitCommit className="h-4 w-4 text-slate-500" />
                      <span>
                        {deploy.latestCommitHash
                          ? deploy.latestCommitHash.substring(0, 7)
                          : "wip"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-300">
                      <Clock className="h-4 w-4 text-slate-500" />
                      <span>
                        {formatDistanceToNow(new Date(deploy.createdAt), {
                          addSuffix: true,
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-400">Assigned domains:</Label>
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Globe className="h-4 w-4 text-slate-500" />
                <span>{deploy.subDomain}</span>
              </div>
            </div>

            {deploy.status === "failed" && (
              <div className="rounded-md border border-yellow-500/30 bg-yellow-500/10 p-3">
                <div className="flex items-center gap-2 text-yellow-500">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    Previous deploy failed
                  </span>
                </div>
                <p className="mt-1 text-xs text-yellow-500/80 pl-6">
                  Check logs before redeploying.
                </p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="p-4 border-t border-slate-800 bg-slate-900/30">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-slate-300 hover:text-white hover:bg-slate-800"
            disabled={isRedeploying}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRedeploy}
            disabled={
              isRedeploying || deploy.status === DEPLOY_STATUS.IN_PROGRESS
            }
            className="hover:bg-slate-200 border-0"
            style={{ backgroundColor: "white", color: "black" }}
          >
            {isRedeploying ? "Redeploying..." : "Redeploy"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
